import CommonModal from "component/Common/CommonModal";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { images } from "helpers/images";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { apiMethods, endPoints } from "service/endpoints";
import { request } from "service/request";
import { SingleSelectComponent } from "component/DesignComponents";
import { description } from "platform";
import FeedbackThankYou from "./FeedbackThankYou";
import RatingChoices from "component/Dashboard/FeedbackNew/RatingChoices";

const FeedbackNew = ({ showFeedback, handleCloseFeedback }) => {
  const source = useRef(axios.CancelToken.source());
  const ratingChoices = [
    { label: "Bad", emoji: "\u{1F614}" },
    { label: "Poor", emoji: "\u{1F641}" },
    { label: "Average", emoji: "\u{1F610}" },
    { label: "Good", emoji: "\u{1F603}" },
    { label: "Excellent", emoji: "\u{1F929}" },
  ];
  const [feedbackStep, setFeedbackStep] = useState(null);
  const [currentPortalStep, setCurrentPortalStep] = useState(null);
  const [feedbackOtherValue, setFeedbackOthervalue] = useState(null);

  const [ratingValues, setRatingValues] = useState({});
  const [optionValues, setOptionValues] = useState({});
  const [otherValue, setOtherValue] = useState(null);
  const [textValues, setTextValues] = useState({});
  const [associationValues, setAssociationValues] = useState({});
  const [improvementReason, setImprovementReason] = useState(null);
  const [improvementOptions, setImprovementOptions] = useState([]);

  const hasLowRatings = ["Bad", "Poor", "Average"];

  const fetchImprovementOptions = async () => {
    try {
      const response = await request({
        url: endPoints.feedback.FILTER_DATA,
        method: apiMethods.GET,
        showToast: false,
        cancelToken: source.current.token,
      });
      if (response.success) {
        setImprovementOptions(response.data?.issue_types || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!showFeedback) {
      setFeedbackStep(null);
      setCurrentPortalStep(null);
      setFeedbackOthervalue(null);
      setRatingValues({});
      setOptionValues({});
      setOtherValue(null);
      setTextValues({});
      setAssociationValues({});
      setImprovementReason(null);
    } else {
      setFeedbackStep("Portal");
      setCurrentPortalStep("ratings");
      fetchImprovementOptions();
    }
  }, [showFeedback]);

  useEffect(() => {
    if (feedbackStep === "ThankYou") {
      submitFeedback();
    }
  }, [feedbackStep]);

  const submitFeedback = async () => {
    const payload = {
      rating: ratingValues["overall_rating"] ?? "",
      issue_type: improvementReason?.value ?? "",
      description: textValues["description"] ?? "",
    };
    try {
      await request({
        url: endPoints.feedback.SUBMIT_FEEDBACK,
        method: apiMethods.POST,
        isLoader: false,
        showToast: false,
        cancelToken: source.current.token,
        data: payload,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleFeedbackPopupClose = () => {
    handleCloseFeedback();
  };

  const FeedbackRatings = ({
    ratingValues,
    handleRatingValues,
    improvementReason,
    setImprovementReason,
  }) => {
    return (
      <div className="flex flex-col gap-6 w-full">
        <div className="flex flex-col items-start w-full gap-6">
          <div className="flex flex-col gap-3 w-5/6">
            <p className="text-xs font-bold">Rate your experience</p>
            <RatingChoices
              ratingChoices={ratingChoices}
              selectedRating={ratingValues["overall_rating"]}
              onSelect={(rating) => handleRatingValues("overall_rating", rating)}
              hasLowRatings={hasLowRatings}
              setImprovementReason={setImprovementReason}
            />
          </div>
          {hasLowRatings.includes(
            ratingValues["overall_rating"] &&
              ratingChoices[ratingValues["overall_rating"] - 1]?.label
          ) && (
            <div className="flex flex-col gap-2 w-full">
              <label className="text-xs font-bold mb-1">Where can we improve?</label>
              <SingleSelectComponent
                selectClass="min-w-full text-sm font-medium"
                selectedOption={improvementReason}
                handleSelection={(option) => setImprovementReason(option)}
                options={
                  Array.isArray(improvementOptions)
                    ? improvementOptions.map((opt) =>
                        typeof opt === "string" ? { label: opt, value: opt } : opt
                      )
                    : []
                }
                placeholder="Select an option"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const FeedbackPortal = ({
    ratingValues,
    setRatingValues,
    improvementReason,
    setImprovementReason,
  }) => {
    const handleRatingValues = (key, rating) => {
      setRatingValues((prevRatings) => ({
        ...prevRatings,
        [key]: rating,
      }));
    };

    return (
      <div className="flex flex-col w-full h-full items-start gap-6 flex-1">
        <p className="text-left text-base text-grey-700 font-medium">
          Leave a message to our Product and User Experience teams. For queries related to
          counselling or payment, please email us at 
          <span className="text-blue">
            <a href="mailto:hello@zynerd.com">hello@zynerd.com</a>
          </span>
          .
        </p>
        <div className="flex flex-col items-center justify-center w-full h-full flex-1">
          <div className="flex h-full w-full overflow-auto mb-10">
            <FeedbackRatings
              ratingValues={ratingValues}
              handleRatingValues={handleRatingValues}
              improvementReason={improvementReason}
              setImprovementReason={setImprovementReason}
            />
          </div>
        </div>
      </div>
    );
  };

  <FeedbackThankYou />;

  const enableNextButton = () => {
    if (currentPortalStep === "ratings" && !Object.values(ratingValues).length > 0) return false;
    else if (currentPortalStep === "options" && !Object.values(optionValues).length > 0)
      return false;
    else {
      return true;
    }
  };

  const handleNextClick = () => {
    setFeedbackStep("ThankYou");
  };

  return (
    <CommonModal
      customDialogModal="rounded-3xl"
      customBodyClass="bg-white rounded-3xl"
      show={showFeedback}
      handleClose={handleCloseFeedback}
      center={true}
      size="lg"
    >
      <div id="feedback-dialog" className="bg-white flex flex-col rounded-3xl">
        <div className="bg-white shadow-[0px_-4px_24px_0px_rgba(0,_0,_0,_0.1)] px-3 py-[18.5px] flex justify-between items-center rounded-t-3xl">
          <h3 className="font-black text-black text-sm">Feedback</h3>
          <div className="bg-grey-50 p-1 rounded-4xl">
            <XMarkIcon
              className="w-6 h-6 cursor-pointer stroke-2"
              onClick={handleFeedbackPopupClose}
            />
          </div>
        </div>
        <div
          id="feedback-body"
          className="flex flex-col p-10 w-full h-full rounded-3xl overflow-y-auto"
        >
          {feedbackStep === "Portal" && (
            <>
              <FeedbackPortal
                ratingValues={ratingValues}
                setRatingValues={setRatingValues}
                optionValues={optionValues}
                setOptionValues={setOptionValues}
                otherValue={otherValue}
                setOtherValue={setOtherValue}
                textValues={textValues}
                setTextValues={setTextValues}
                associationValues={associationValues}
                setAssociationValues={setAssociationValues}
                currentPortalStep={currentPortalStep}
                setCurrentPortalStep={setCurrentPortalStep}
                setFeedbackOthervalue={setFeedbackOthervalue}
                feedbackOtherValue={feedbackOtherValue}
                improvementReason={improvementReason}
                setImprovementReason={setImprovementReason}
              />

              <div className="flex flex-col gap-3 w-full">
                <p className="text-xs font-bold">Message</p>
                <textarea
                  className="!py-2 !px-2 border border-grey-border bg-grey-25 rounded-xl text-sm font-medium w-full min-h-40 focus:outline-0"
                  value={textValues["description"]}
                  placeholder="Enter message..."
                  name="description"
                  maxLength={4000}
                  onChange={(e) =>
                    setTextValues((prev) => ({ ...prev, description: e.target.value }))
                  }
                />
              </div>
            </>
          )}
          {feedbackStep === "ThankYou" && <FeedbackThankYou />}
        </div>
        {(feedbackStep === "Portal" || feedbackStep === "ThankYou") && (
          <div
            className="flex justify-end p-3 w-full bg-white shadow-[0px_-4px_24px_0px_rgba(0,_0,_0,_0.1)] rounded-b-3xl"
            style={{ margin: 0 }}
          >
            <div
              className={`text-xs font-bold text-white px-6 py-2 rounded-4xl ${
                feedbackStep === "Portal"
                  ? enableNextButton()
                    ? "bg-[#F46C3C] cursor-pointer"
                    : "bg-[#D9D9D9] cursor-not-allowed"
                  : "bg-[#F46C3C] cursor-pointer"
              }`}
              onClick={() => {
                if (feedbackStep === "Portal") {
                  if (enableNextButton()) handleNextClick();
                } else if (feedbackStep === "ThankYou") {
                  handleFeedbackPopupClose();
                }
              }}
            >
              {feedbackStep === "Portal" ? "Submit" : "Okay"}
            </div>
          </div>
        )}
      </div>
    </CommonModal>
  );
};

export default FeedbackNew;
